import { useKeyboardControls } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { useRapier, CapsuleCollider, RigidBody } from "@react-three/rapier";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { MathUtils, Vector3 } from "three";
import { degToRad } from "three/src/math/MathUtils.js";
import Character from "./Character.jsx";
import Turtle from "./Turtle.jsx";
import { normalizeAngle, lerpAngle } from "../../utils/playerMovement.js";
import { useModal } from "../../contexts/ModalContext.jsx";

// Memoize initial vectors
const initialCameraWorldPosition = new Vector3();
const initialCameraLookAtWorldPosition = new Vector3();
const initialCameraLookAt = new Vector3();

const Player = React.memo(() => {
    // Defaults Without Debug Menu
    const WALK_SPEED = 1.6;
    const RUN_SPEED = 2.8;
    const ROTATION_SPEED = degToRad(1.1);

    // Camera and Player values/states
    const characterRotationTarget = useRef(0);
    const rotationTarget = useRef(0);
    const cameraTarget = useRef();
    const cameraPosition = useRef();
    const cameraWorldPosition = useRef(initialCameraWorldPosition);
    const cameraLookAtWorldPosition = useRef(initialCameraLookAtWorldPosition);
    const cameraLookAt = useRef(initialCameraLookAt);
    const body = useRef();
    const container = useRef();
    const characterRef = useRef();
    const isClicking = useRef(false);

    // Animation and turtle states
    const [turtles, setTurtles] = useState([]);
    const [animation, setAnimation] = useState("idle"); 

    // Physics vars 
    const [subscribeKeys, get] = useKeyboardControls();
    const { rapier, world } = useRapier();
    const rapierWorld = world;
    const isGrounded = useRef(false);
    const canJump = useRef(true);

    // throttle ground checks
    const lastGroundCheck = useRef(0);
    const groundCheckInterval = 0.05; 

    // Modal
    const { isOpen } = useModal();

    // Handle Turtle Placement with optimization
    const placeTurtle = useCallback(() => {
        if (body.current) {
            const playerPosition = body.current.translation();
            const rotation = container.current.rotation.y;

            const turtlePosition = {
                x: playerPosition.x + Math.sin(rotation) * 1,
                y: playerPosition.y + 0.5,
                z: playerPosition.z + Math.cos(rotation) * 1
            };

            setTurtles(prevTurtles => {
                // Limit number of turtles to prevent memory issues
                const maxTurtles = 20;
                const newTurtles = [...prevTurtles, turtlePosition];
                return newTurtles.length > maxTurtles ? newTurtles.slice(-maxTurtles) : newTurtles;
            });
        }
    }, []);

    const checkGrounded = useCallback(() => {
        if(!body.current) return false;

        const origin = body.current.translation();
        const ray = new rapier.Ray({ x: origin.x, y: origin.y, z: origin.z }, { x: 0, y: -1, z: 0 });
        const hit = rapierWorld.castRay(ray, 0.3, true);
        
        return hit !== null;
    }, [rapier, rapierWorld]);

    const jump = useCallback(() => {
        if(!isGrounded.current || !canJump.current){
            return;
        }

        setAnimation("Jump_Idle");
        body.current.applyImpulse({ x: 0, y: 0.2, z: 0 });

        canJump.current = false;
        setTimeout(() => {
            canJump.current = true;
        }, 1000);
    }, []);

    // Event Handlers - jump and spawn turtle currently implemented
    const handleKeyPress = useCallback((event) => {
        if (event.key === ' ') {
            jump();
        }
        else if (event.key === 'f') {
            setAnimation("2H_Ranged_Shoot");
            placeTurtle();
        }
    }, [jump, placeTurtle]);

    useEffect(() => {
        window.addEventListener('keypress', handleKeyPress);
        return () => window.removeEventListener('keypress', handleKeyPress);
    }, [handleKeyPress]);

    useEffect(() => {
        const handleJump = () => {
            jump();
        };

        const unsubscribeJump = subscribeKeys(
            (state) => {
                return state.jump;
            },
            (value) => {
                handleJump();
            }
        );

        return () => {
            unsubscribeJump();
        };
    }, [jump, subscribeKeys]);

    // Click and Mouse Event Listeners
    useEffect(() => {
        const onMouseDown = (e) => {
            isClicking.current = true;
        };
        const onMouseUp = (e) => {
            isClicking.current = false;
        };

        document.addEventListener("mousedown", onMouseDown);
        document.addEventListener("mouseup", onMouseUp);
        document.addEventListener("touchstart", onMouseDown);
        document.addEventListener("touchend", onMouseUp);
        return () => {
            document.removeEventListener("mousedown", onMouseDown);
            document.removeEventListener("mouseup", onMouseUp);
            document.removeEventListener("touchstart", onMouseDown);
            document.removeEventListener("touchend", onMouseUp);
        };
    }, []);

    // Memoize movement calculations 
    const updateFrame = useCallback(({ camera, mouse, clock }) => {
        if(!window.gameStarted) return;

        // stop movements when a billboard is open
        if(isOpen) {
            if(body.current) {
                const vel = body.current.linvel();
                vel.x = 0;
                vel.z = 0;
                body.current.setLinvel(vel, true);
            }

            // Still update camera position
            if (cameraPosition.current && cameraTarget.current) {
                cameraPosition.current.getWorldPosition(cameraWorldPosition.current);
                camera.position.lerp(cameraWorldPosition.current, 0.1);
                cameraTarget.current.getWorldPosition(cameraLookAtWorldPosition.current);
                cameraLookAt.current.lerp(cameraLookAtWorldPosition.current, 0.1);
                camera.lookAt(cameraLookAt.current);
            }
            return;
        }

        if (body.current) {
            // Throttled ground check
            if (clock.elapsedTime - lastGroundCheck.current > groundCheckInterval) {
                isGrounded.current = checkGrounded();
                lastGroundCheck.current = clock.elapsedTime;
            }

            const vel = body.current.linvel();
            const currentPosition = body.current.translation();
            
            // Update global player position for other components (like timeline)
            window.playerPosition = currentPosition;

            const movement = {
                x: 0,
                z: 0,
            };

            // WASD or Arrow keys
            if (get().forward) {
                movement.z = 1;
            }
            if (get().backward) {
                movement.z = -1;
            }
            if (get().leftward) {
                movement.x = 5;
            }
            if (get().rightward) {
                movement.x = -5;
            }

            let speed = get().run ? RUN_SPEED : WALK_SPEED;

            if (isClicking.current) {
                if (Math.abs(mouse.x) > 0.1) {
                    movement.x = -mouse.x;
                }
                movement.z = mouse.y + 0.4;
                if (Math.abs(movement.x) > 0.5 || Math.abs(movement.z) > 0.5) {
                    speed = RUN_SPEED;
                }
            }

            if (movement.x !== 0) {
                rotationTarget.current += ROTATION_SPEED * movement.x;
            }

            if (movement.x !== 0 || movement.z !== 0) {
                characterRotationTarget.current = Math.atan2(movement.x, movement.z);
                vel.x = Math.sin(rotationTarget.current + characterRotationTarget.current) * speed;
                vel.z = Math.cos(rotationTarget.current + characterRotationTarget.current) * speed;
                if (speed === RUN_SPEED) {
                    setAnimation("Running_A");
                } else {
                    setAnimation("Walking_A");
                }
            } else {
                setAnimation("Idle");
            }

            characterRef.current.rotation.y = lerpAngle(
                characterRef.current.rotation.y,
                characterRotationTarget.current,
                0.1
            );

            body.current.setLinvel(vel, true);
        }
        
        container.current.rotation.y = MathUtils.lerp(
            container.current.rotation.y,
            rotationTarget.current,
            0.1
        );

        // CAMERA
        cameraPosition.current.getWorldPosition(cameraWorldPosition.current);
        camera.position.lerp(cameraWorldPosition.current, 0.1);

        if (cameraTarget.current) {
            cameraTarget.current.getWorldPosition(cameraLookAtWorldPosition.current);
            cameraLookAt.current.lerp(cameraLookAtWorldPosition.current, 0.1);
            camera.lookAt(cameraLookAt.current);
        }

        // Handle if the player falls off the map
        // Also handle when the player reaches the end and camera effect
        if(body.current) {
            const position = body.current.translation();
            if(position.y < -10) {
                body.current.setTranslation({ x: 0, y: 5, z: 0 });
                body.current.setLinvel({ x: 0, y: 0, z: 0 });
            }
            if(position.x < -25) {
                camera.fov = MathUtils.lerp(camera.fov, 85, 0.05);
                camera.updateProjectionMatrix();
                if(cameraTarget.current) {
                    cameraTarget.current.position.y = MathUtils.lerp(
                        cameraTarget.current.position.y, 
                        2,
                        0.05
                    );
                }
            } else {
                camera.fov = MathUtils.lerp(camera.fov, 75, 0.05); 
                camera.updateProjectionMatrix();
                if(cameraTarget.current) {
                    cameraTarget.current.position.y = MathUtils.lerp(
                        cameraTarget.current.position.y, 
                        0,
                        0.05
                    );
                }
            }
        }
    }, [checkGrounded, get, isOpen]);

    useFrame((state) => {
        updateFrame(state);
    });

    return (
        <>
            <RigidBody colliders={false} lockRotations ref={body}>
                <group ref={container}>
                    <group ref={cameraTarget} position-z={1.5} />
                    <group ref={cameraPosition} position-y={1} position-z={-3} />
                    <group ref={characterRef}> 
                        <Character position-y={-0.25} animation={animation} />
                    </group>
                </group>
                <CapsuleCollider args={[0.12, 0.18]} />
            </RigidBody>

            {/* Render turtles with performance consideration */}
            {turtles.map((position, index) => (
                <RigidBody
                    key={index}
                    type="dynamic"
                    position={[position.x, position.y, position.z]}
                    colliders="cuboid"
                    friction={0.7}
                >
                    <Turtle />
                </RigidBody>
            ))}
        </>
    );
});

export default Player;